import React, { useState } from "react";
import { Box, Button, Select } from "@chakra-ui/react";
import { Tooltip } from "@mui/material";
import InputField from "components/fields/InputField";
import MerchantListModal from "../../shared/MerchantListModal";
import SubaccountListModal from "../../shared/SubaccountListModal";

const ReceiverSearch: React.FC<{
  showFilters: boolean;
  isLoading: boolean;
  filters: any;
  setFilters: any;
  handleSearchSubmit(): void;
  handleSearchClear(): void;
  isFilterLoading: boolean;
}> = ({
  showFilters,
  isLoading,
  filters,
  setFilters,
  handleSearchSubmit,
  handleSearchClear,
  isFilterLoading,
}) => {
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  // Modal states for merchant and subaccount selection
  const [isMerchantModalOpen, setIsMerchantModalOpen] = useState<boolean>(false);
  const [isSubaccountModalOpen, setIsSubaccountModalOpen] = useState<boolean>(false);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);
  const [selectedSubaccount, setSelectedSubaccount] = useState<any>(null);

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;
    setFilters({ ...filters, [id]: value });
  };

  // Handler for merchant selection from modal
  const handleMerchantSelect = (merchantData: any) => {
    setSelectedMerchant(merchantData);
    setSelectedSubaccount(null);
    localStorage.setItem("merchantIdToFilterReceiver", merchantData.id);
    localStorage.setItem("subaccountIdToFilterReceiver", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  // Clear merchant selection
  const handleClearMerchant = () => {
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    localStorage.setItem("merchantIdToFilterReceiver", "");
    localStorage.setItem("subaccountIdToFilterReceiver", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  // Handler for subaccount selection from modal
  const handleSubaccountSelect = (subaccountData: any) => {
    setSelectedSubaccount(subaccountData);
    localStorage.setItem("subaccountIdToFilterReceiver", subaccountData.id);
    setFilters({ ...filters, subaccount_id: subaccountData.id });
  };

  // Clear subaccount selection
  const handleClearSubaccount = () => {
    setSelectedSubaccount(null);
    localStorage.setItem("subaccountIdToFilterReceiver", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  const handleClear = () => {
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    localStorage.setItem("merchantIdToFilterReceiver", "");
    localStorage.setItem("subaccountIdToFilterReceiver", "");
    handleSearchClear();
  };

  // If not showing filters, render nothing (button lives outside in the toolbar)
  if (!showFilters) return null;
  return (
    <Box className="mb-4 mt-2 rounded-lg bg-white p-6 shadow-lg ring-1 ring-gray-100">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Field
          </label>
          <Select
            isDisabled={isLoading}
            id="field"
            value={filters.field}
            onChange={handleInputChange}
            className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
          >
            <option value="">Select Field</option>
            <option value="first_name">First Name</option>
            <option value="last_name">Last Name</option>
            <option value="email">Email</option>
            <option value="receiver_id">Receiver ID</option>
            <option value="receiver_account_id">Receiver Account ID</option>
          </Select>
        </div>
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Keyword
          </label>
          <InputField
            disabled={isLoading}
            variant="auth"
            extra="shadow-sm rounded-md"
            label=""
            placeholder=""
            id="keyword"
            type="text"
            autoComplete="off"
            value={filters.keyword}
            onChange={handleInputChange}
          />
        </div>
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Status
          </label>
          <Select
            isDisabled={isLoading}
            id="status"
            value={filters.status}
            onChange={handleInputChange}
            className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
          >
            <option value="">All</option>
            <option value="0">Creating</option>
            <option value="1">Pending</option>
            <option value="2">Active</option>
            <option value="3">Inactive</option>
            <option value="4">Failed</option>
          </Select>
        </div>
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            KYC
          </label>
          <Select
            isDisabled={isLoading}
            id="kyc_status"
            value={filters.kyc_status}
            onChange={handleInputChange}
            className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
          >
            <option value="">All</option>
            <option value="0">Pending</option>
            <option value="1">Complete</option>
            <option value="2">Failed</option>
          </Select>
        </div>
        {userRole === "superadmin" && (
          <>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Merchant
              </label>
              <Tooltip
                title={selectedMerchant ? selectedMerchant.name : "All"}
                arrow
                placement="top"
              >
                <div
                  onClick={() => !isFilterLoading && setIsMerchantModalOpen(true)}
                  className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                    isFilterLoading
                      ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                      : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                  }`}
                >
                  <span className="truncate">
                    {selectedMerchant ? selectedMerchant.name : "All"}
                  </span>
                  {selectedMerchant && !isFilterLoading && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleClearMerchant(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </Tooltip>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Subaccount
              </label>
              <Tooltip
                title={selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                arrow
                placement="top"
              >
                <div
                  onClick={() => !isFilterLoading && selectedMerchant && setIsSubaccountModalOpen(true)}
                  className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                    isFilterLoading || !selectedMerchant
                      ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                      : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                  }`}
                >
                  <span className="truncate">
                    {selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                  </span>
                  {selectedSubaccount && !isFilterLoading && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </Tooltip>
            </div>
            <div className="mt-4 flex items-end justify-end space-x-4 md:col-span-2">
              <Button
                isDisabled={isLoading}
                onClick={() => handleClear()}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                Clear
              </Button>

              <Button
                isDisabled={isLoading}
                onClick={() => handleSearchSubmit()}
                className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="relative z-10 tracking-tight">
                  Search Receivers
                </span>
                <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
              </Button>
            </div>
          </>
        )}
      </div>
      {userRole !== "superadmin" && (
        <div className="mt-4 flex flex-1 items-end justify-end">
          <Button
            isDisabled={isLoading}
            onClick={() => handleClear()}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
          >
            Clear
          </Button>

          <Button
            isDisabled={isLoading}
            onClick={() => handleSearchSubmit()}
            className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="relative z-10 tracking-tight">
              Search Receivers
            </span>
            <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
          </Button>
        </div>
      )}

      {/* Merchant Selection Modal */}
      {isMerchantModalOpen && (
        <MerchantListModal
          isOpen={isMerchantModalOpen}
          onClose={() => setIsMerchantModalOpen(false)}
          onSelect={handleMerchantSelect}
        />
      )}

      {/* Subaccount Selection Modal */}
      {isSubaccountModalOpen && selectedMerchant && (
        <SubaccountListModal
          isOpen={isSubaccountModalOpen}
          onClose={() => setIsSubaccountModalOpen(false)}
          onSelect={handleSubaccountSelect}
          merchantId={selectedMerchant.id}
        />
      )}
    </Box>
  );
};

export default ReceiverSearch;
